import { EventsKey } from 'ol/events';
import OLMap from 'ol/Map';
import { SOURCE_CHANGE_EVENT_NAME } from './map-type';
import TileLayer from 'ol/layer/Tile';
import Source from 'ol/source/Source';
import UrlTile from 'ol/source/UrlTile';
import Tile, { LoadFunction, UrlFunction } from 'ol/Tile';
import { TileCoord } from 'ol/tilecoord';
import TileState from 'ol/TileState';
import Projection from 'ol/proj/Projection';
import { ImageTile } from 'ol';

export interface IStormYearsColor {
    color: string;
    textColor: string;
    translationKey: string;
    rtype: FrequencyType
}

export interface MapRain {
    lid: number;
    res: number;
    runit?: string;
    rtype?: number;
    /** Whenever show graph. Threat as TRUE by default (when value=undefined | null) */
    showBar?: boolean;
    /** Whenever show empty values. Threat as TRUE by default (when value=undefined | null). */
    showEmpty?: boolean;
}

export const MapRainColors: {
    color: string;
    textColor: string;
    value: number;
    range: Array<number>
}[] = [
    {color: '#ffffff', textColor: '#000000', value: 8, range: [8, Number.MAX_VALUE]},
    {color: '#9854bd', textColor: '#ffffff', value: 6, range: [6, 8]},
    {color: '#f800fd', textColor: '#ffffff', value: 4, range: [4, 6]},
    {color: '#bc0000', textColor: '#ffffff', value: 3, range: [3, 4]},
    {color: '#d40000', textColor: '#ffffff', value: 2.5, range: [2.5, 3]},
    {color: '#d40000', textColor: '#ffffff', value: 1.7, range: [1.7, 2.5]},
    {color: '#e5bc00', textColor: '#000000', value: 1.5, range: [1.5, 1.7]},
    {color: '#fdf802', textColor: '#000000', value: 1.2, range: [1.2, 1.5]},
    {color: '#008e00', textColor: '#ffffff', value: 1, range: [1, 1.2]},
    {color: '#01c501', textColor: '#000000', value: 0.75, range: [0.75, 1]},
    {color: '#02fd02', textColor: '#000000', value: 0.5, range: [0.5, 0.75]},
    {color: '#0300f4', textColor: '#ffffff', value: 0.25, range: [0.25, 0.5]},
    {color: '#019ff4', textColor: '#ffffff', value: 0.1, range: [0.1, 0.25]},
    {color: '#646464', textColor: '#ffffff', value: 0, range: [Number.MIN_VALUE, 0.1]}
];

export enum FrequencyType {
    LessThenOne = 0,
    OneToTwo,
    TwoToFive,
    FiveToTen,
    TenToTwentyFive,
    TwentyFiveToFifty,
    FiftyToHundred,
    OneHundredToTwoHundred,
    TwoHundredToFiveHundred,
    FiveHundredToThousand,
    GreaterThenThousand
}

export const StormYearsColors: IStormYearsColor[] = [
    {color: '#ffffff', textColor: '#000000', translationKey: 'LessThenOne', rtype: FrequencyType.LessThenOne},
    {color: '#DAEED8', textColor: '#000000', translationKey: 'OneToTwo', rtype: FrequencyType.OneToTwo},
    {color: '#45A065', textColor: '#000000', translationKey: 'TwoToFive', rtype: FrequencyType.TwoToFive},
    {color: '#09662B', textColor: '#ffffff', translationKey: 'FiveToTen', rtype: FrequencyType.FiveToTen},
    {color: '#FFF987', textColor: '#000000', translationKey: 'TenToTwentyFive', rtype: FrequencyType.TenToTwentyFive},
    {color: '#F9CB52', textColor: '#000000', translationKey: 'TwentyFiveToFifty', rtype: FrequencyType.TwentyFiveToFifty},
    {color: '#FD8C34', textColor: '#000000', translationKey: 'FiftyToHundred', rtype: FrequencyType.FiftyToHundred},
    {color: '#FB4F2E', textColor: '#000000', translationKey: 'OneHundredToTwoHundred', rtype: FrequencyType.OneHundredToTwoHundred},
    {color: '#A40D0D', textColor: '#ffffff', translationKey: 'TwoHundredToFiveHundred', rtype: FrequencyType.TwoHundredToFiveHundred},
    {color: '#4D0072', textColor: '#ffffff', translationKey: 'FiveHundredToThousand', rtype: FrequencyType.FiveHundredToThousand},
    {color: '#FDDDF9', textColor: '#000000', translationKey: 'GreaterThenThousand', rtype: FrequencyType.GreaterThenThousand}
];


const TILE_ERROR_DEBOUNCE_MS = 500;
export const SAFE_MAX_ZOOM_LEVEL = 19;
const ARCGIS_PLACEHOLDER_TILE_SIZE = 2521;
const ARCGIS_TILE_MAP_BLOCK_SIZE = 32;
const ARCGIS_TILE_MAP_CACHE_LIMIT = 512;
const ARCGIS_CACHED_TILE_PATH_REGEX = /^(.*\/(?:MapServer|ImageServer))\/tile\/(\d+)\/(\d+)\/(\d+)\/?$/i;

interface ArcGISCachedTileRequest {
    serviceUrl: string;
    level: number;
    row: number;
    column: number;
    url: URL;
}

interface ArcGISTileMapBlock {
    top: number;
    left: number;
    width: number;
    height: number;
    data: number[];
}

export interface BasemapTileLoadOptions {
    onAuthenticationError?: () => void;
}

let lastZoomOutTime = 0;
let onZoomOutCallback: (() => void) | null = null;

const configuredBasemapLayers = new WeakSet<TileLayer>();
const configuredBasemapSources = new WeakSet<UrlTile>();
const tileMapBlocks = new Map<string, ArcGISTileMapBlock>();
const pendingTileMapBlocks = new Map<string, Promise<ArcGISTileMapBlock | null>>();
const unsupportedTileMapServices = new Set<string>();

function isPlaceholderTile(blob: Blob, currentZoom: number | undefined): boolean {
    return blob.size === ARCGIS_PLACEHOLDER_TILE_SIZE && currentZoom !== undefined && currentZoom > SAFE_MAX_ZOOM_LEVEL;
}

function zoomToSafeLevel(map: OLMap): void {
    const now = Date.now();
    const timeSinceLastZoom = now - lastZoomOutTime;

    if (timeSinceLastZoom < TILE_ERROR_DEBOUNCE_MS) {
        return;
    }

    const view = map.getView();
    const currentZoom = view.getZoom();

    if (currentZoom === undefined || currentZoom <= SAFE_MAX_ZOOM_LEVEL) {
        return;
    }

    lastZoomOutTime = now;
    view.setZoom(SAFE_MAX_ZOOM_LEVEL);

    if (onZoomOutCallback) {
        onZoomOutCallback();
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function isUnknownArray(value: unknown): value is readonly unknown[] {
    return Array.isArray(value);
}

function getNumberValue(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function getArcGISErrorCode(value: unknown): number | null {
    if (!isRecord(value) || !isRecord(value.error)) {
        return null;
    }

    return getNumberValue(value.error.code);
}

function isArcGISAuthenticationErrorCode(code: number): boolean {
    return code === 498 || code === 499 || code === 401 || code === 403;
}

function isUnsupportedTileMapStatus(status: number): boolean {
    return status === 400 || status === 404 || status === 501;
}

function getTileMapCacheKey(serviceUrl: string, level: number, top: number, left: number, width: number, height: number): string {
    return `${serviceUrl}|${level}|${top}|${left}|${width}|${height}`;
}

function setTileMapBlockCache(cacheKey: string, block: ArcGISTileMapBlock): void {
    tileMapBlocks.set(cacheKey, block);

    if (tileMapBlocks.size <= ARCGIS_TILE_MAP_CACHE_LIMIT) {
        return;
    }

    for (const key of tileMapBlocks.keys()) {
        tileMapBlocks.delete(key);
        break;
    }
}

function parseArcGISCachedTileUrl(src: string): ArcGISCachedTileRequest | null {
    let url: URL;

    try {
        url = new URL(src, window.location.href);
    } catch {
        return null;
    }

    const match = ARCGIS_CACHED_TILE_PATH_REGEX.exec(url.pathname);

    if (!match) {
        return null;
    }

    const level = Number(match[2]);
    const row = Number(match[3]);
    const column = Number(match[4]);

    if (!Number.isInteger(level) || !Number.isInteger(row) || !Number.isInteger(column)) {
        return null;
    }

    return {
        serviceUrl: `${url.origin}${match[1]}`,
        level,
        row,
        column,
        url,
    };
}

function createTileMapUrl(tileRequest: ArcGISCachedTileRequest, top: number, left: number, width: number, height: number): string {
    const tileMapUrl = new URL(`${tileRequest.serviceUrl}/tilemap/${tileRequest.level}/${top}/${left}/${width}/${height}`);

    tileRequest.url.searchParams.forEach((value: string, key: string) => {
        tileMapUrl.searchParams.append(key, value);
    });

    tileMapUrl.searchParams.set('f', 'json');

    return tileMapUrl.toString();
}

function createEmptyTileMapBlock(top: number, left: number, width: number, height: number): ArcGISTileMapBlock {
    return {
        top,
        left,
        width,
        height,
        data: new Array<number>(width * height).fill(0),
    };
}

function parseTileMapBlock(value: unknown): ArcGISTileMapBlock | null {
    if (!isRecord(value) || !isRecord(value.location) || !isUnknownArray(value.data)) {
        return null;
    }

    const top = getNumberValue(value.location.top);
    const left = getNumberValue(value.location.left);
    const width = getNumberValue(value.location.width);
    const height = getNumberValue(value.location.height);

    if (top === null || left === null || width === null || height === null) {
        return null;
    }

    const data: number[] = [];

    for (const item of value.data) {
        const numberValue = getNumberValue(item);

        if (numberValue === null) {
            return null;
        }

        data.push(numberValue);
    }

    return {
        top,
        left,
        width,
        height,
        data,
    };
}

function readAvailabilityFromBlock(tileRequest: ArcGISCachedTileRequest, block: ArcGISTileMapBlock | null): boolean | null {
    if (!block) {
        return null;
    }

    const rowOffset = tileRequest.row - block.top;
    const columnOffset = tileRequest.column - block.left;

    if (rowOffset < 0 || columnOffset < 0 || rowOffset >= block.height || columnOffset >= block.width) {
        return null;
    }

    const index = rowOffset * block.width + columnOffset;
    const value = block.data[index];

    return value === undefined ? null : value === 1;
}

function getCachedArcGISTileAvailability(tileRequest: ArcGISCachedTileRequest): boolean | null {
    const blockTop = Math.floor(tileRequest.row / ARCGIS_TILE_MAP_BLOCK_SIZE) * ARCGIS_TILE_MAP_BLOCK_SIZE;
    const blockLeft = Math.floor(tileRequest.column / ARCGIS_TILE_MAP_BLOCK_SIZE) * ARCGIS_TILE_MAP_BLOCK_SIZE;
    const blockKey = getTileMapCacheKey(tileRequest.serviceUrl, tileRequest.level, blockTop, blockLeft, ARCGIS_TILE_MAP_BLOCK_SIZE, ARCGIS_TILE_MAP_BLOCK_SIZE);

    if (tileMapBlocks.has(blockKey)) {
        const blockAvailability = readAvailabilityFromBlock(tileRequest, tileMapBlocks.get(blockKey) || null);

        if (blockAvailability !== null) {
            return blockAvailability;
        }
    }

    const tileKey = getTileMapCacheKey(tileRequest.serviceUrl, tileRequest.level, tileRequest.row, tileRequest.column, 1, 1);

    if (tileMapBlocks.has(tileKey)) {
        return readAvailabilityFromBlock(tileRequest, tileMapBlocks.get(tileKey) || null);
    }

    return null;
}

async function readResponseJson(response: Response): Promise<unknown> {
    try {
        const responseBody: unknown = await response.clone().json();
        return responseBody;
    } catch {
        return null;
    }
}

async function fetchArcGISTileMapBlock(
    tileRequest: ArcGISCachedTileRequest,
    top: number,
    left: number,
    width: number,
    height: number,
    options: BasemapTileLoadOptions
): Promise<ArcGISTileMapBlock | null> {
    try {
        const response = await fetch(createTileMapUrl(tileRequest, top, left, width, height), { mode: 'cors' });

        if (isArcGISAuthenticationErrorCode(response.status)) {
            if (options.onAuthenticationError) {
                options.onAuthenticationError();
            }
            return null;
        }

        if (response.status === 422) {
            return createEmptyTileMapBlock(top, left, width, height);
        }

        if (!response.ok) {
            if (isUnsupportedTileMapStatus(response.status)) {
                unsupportedTileMapServices.add(tileRequest.serviceUrl);
            }
            return null;
        }

        const responseBody = await readResponseJson(response);
        const errorCode = getArcGISErrorCode(responseBody);

        if (errorCode !== null) {
            if (isArcGISAuthenticationErrorCode(errorCode) && options.onAuthenticationError) {
                options.onAuthenticationError();
            }

            if (errorCode === 422) {
                return createEmptyTileMapBlock(top, left, width, height);
            }

            if (isUnsupportedTileMapStatus(errorCode)) {
                unsupportedTileMapServices.add(tileRequest.serviceUrl);
            }

            return null;
        }

        return parseTileMapBlock(responseBody);
    } catch {
        return null;
    }
}

async function getArcGISTileMapBlock(
    tileRequest: ArcGISCachedTileRequest,
    top: number,
    left: number,
    width: number,
    height: number,
    options: BasemapTileLoadOptions
): Promise<ArcGISTileMapBlock | null> {
    if (unsupportedTileMapServices.has(tileRequest.serviceUrl)) {
        return null;
    }

    const cacheKey = getTileMapCacheKey(tileRequest.serviceUrl, tileRequest.level, top, left, width, height);

    if (tileMapBlocks.has(cacheKey)) {
        return tileMapBlocks.get(cacheKey) || null;
    }

    const pendingBlock = pendingTileMapBlocks.get(cacheKey);

    if (pendingBlock) {
        return pendingBlock;
    }

    const pendingRequest = fetchArcGISTileMapBlock(tileRequest, top, left, width, height, options);
    pendingTileMapBlocks.set(cacheKey, pendingRequest);

    const block = await pendingRequest;

    if (block) {
        setTileMapBlockCache(cacheKey, block);
    }

    pendingTileMapBlocks.delete(cacheKey);

    return block;
}

async function getArcGISTileAvailability(tileRequest: ArcGISCachedTileRequest, options: BasemapTileLoadOptions): Promise<boolean | null> {
    const cachedAvailability = getCachedArcGISTileAvailability(tileRequest);

    if (cachedAvailability !== null) {
        return cachedAvailability;
    }

    const blockTop = Math.floor(tileRequest.row / ARCGIS_TILE_MAP_BLOCK_SIZE) * ARCGIS_TILE_MAP_BLOCK_SIZE;
    const blockLeft = Math.floor(tileRequest.column / ARCGIS_TILE_MAP_BLOCK_SIZE) * ARCGIS_TILE_MAP_BLOCK_SIZE;
    const block = await getArcGISTileMapBlock(tileRequest, blockTop, blockLeft, ARCGIS_TILE_MAP_BLOCK_SIZE, ARCGIS_TILE_MAP_BLOCK_SIZE, options);
    const blockAvailability = readAvailabilityFromBlock(tileRequest, block);

    if (blockAvailability !== null) {
        return blockAvailability;
    }

    const tileBlock = await getArcGISTileMapBlock(tileRequest, tileRequest.row, tileRequest.column, 1, 1, options);
    return readAvailabilityFromBlock(tileRequest, tileBlock);
}

function setImageTileBlob(tile: ImageTile, blob: Blob, errorState: TileState): void {
    const image = tile.getImage();

    if (!(image instanceof HTMLImageElement)) {
        tile.setState(errorState);
        return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
        const result = reader.result;

        if (typeof result === 'string') {
            image.src = result;
        } else {
            tile.setState(errorState);
        }
    };
    reader.onerror = () => {
        tile.setState(errorState);
    };
    reader.readAsDataURL(blob);
}

async function handleNonImageResponse(response: Response, options: BasemapTileLoadOptions): Promise<void> {
    if (isArcGISAuthenticationErrorCode(response.status) && options.onAuthenticationError) {
        options.onAuthenticationError();
        return;
    }

    const responseBody = await readResponseJson(response);
    const errorCode = getArcGISErrorCode(responseBody);

    if (errorCode !== null && isArcGISAuthenticationErrorCode(errorCode) && options.onAuthenticationError) {
        options.onAuthenticationError();
    }
}

function loadDefaultImageTile(tile: Tile, src: string): void {
    if (!(tile instanceof ImageTile)) {
        tile.setState(TileState.ERROR);
        return;
    }

    const image = tile.getImage();

    if (image instanceof HTMLImageElement) {
        image.src = src;
    } else {
        tile.setState(TileState.ERROR);
    }
}

async function loadSilentArcGISTile(tile: Tile, src: string, tileRequest: ArcGISCachedTileRequest, options: BasemapTileLoadOptions): Promise<void> {
    if (!(tile instanceof ImageTile)) {
        tile.setState(TileState.EMPTY);
        return;
    }

    const availability = await getArcGISTileAvailability(tileRequest, options);

    if (availability === false) {
        tile.setState(TileState.EMPTY);
        return;
    }

    try {
        const response = await fetch(src, { mode: 'cors' });

        if (!response.ok) {
            await handleNonImageResponse(response, options);
            tile.setState(TileState.EMPTY);
            return;
        }

        const contentType = response.headers.get('content-type');

        if (contentType && !contentType.toLowerCase().startsWith('image/')) {
            await handleNonImageResponse(response, options);
            tile.setState(TileState.EMPTY);
            return;
        }

        const blob = await response.blob();
        setImageTileBlob(tile, blob, TileState.EMPTY);
    } catch {
        tile.setState(TileState.EMPTY);
    }
}

export function createSilentBasemapTileLoadFunction(options: BasemapTileLoadOptions = {}): LoadFunction {
    return (tile: Tile, src: string): void => {
        const tileRequest = parseArcGISCachedTileUrl(src);

        if (!tileRequest) {
            loadDefaultImageTile(tile, src);
            return;
        }

        void loadSilentArcGISTile(tile, src, tileRequest, options);
    };
}

export function configureBasemapTileSource(source: Source | null | undefined, options: BasemapTileLoadOptions = {}): boolean {
    if (!(source instanceof UrlTile) || configuredBasemapSources.has(source)) {
        return false;
    }

    configuredBasemapSources.add(source);
    source.setTileLoadFunction(createSilentBasemapTileLoadFunction(options));

    const tileUrlFunction: UrlFunction = source.getTileUrlFunction();
    const guardedTileUrlFunction: UrlFunction = (tileCoord: TileCoord, pixelRatio: number, projection: Projection): string | undefined => {
        const src = tileUrlFunction(tileCoord, pixelRatio, projection);

        if (!src) {
            return undefined;
        }

        const tileRequest = parseArcGISCachedTileUrl(src);

        if (!tileRequest) {
            return src;
        }

        const availability = getCachedArcGISTileAvailability(tileRequest);

        return availability === false ? undefined : src;
    };

    source.setTileUrlFunction(guardedTileUrlFunction);

    return true;
}

export function configureBasemapTileLayer(layer: TileLayer, options: BasemapTileLoadOptions = {}): EventsKey[] {
    const subscriptions: EventsKey[] = [];

    configureBasemapTileSource(layer.getSource(), options);

    if (configuredBasemapLayers.has(layer)) {
        return subscriptions;
    }

    configuredBasemapLayers.add(layer);
    subscriptions.push(layer.on(SOURCE_CHANGE_EVENT_NAME, () => {
        configureBasemapTileSource(layer.getSource(), options);
    }));

    return subscriptions;
}

export function createBasemapTileLoadFunction(
    map: OLMap,
    onZoomOut?: () => void
): LoadFunction {
    if (onZoomOut) {
        onZoomOutCallback = onZoomOut;
    }

    return (tile: Tile, src: string): void => {
        if (!(tile instanceof ImageTile)) {
            tile.setState(TileState.ERROR);
            zoomToSafeLevel(map);
            return;
        }

        fetch(src, { mode: 'cors' })
            .then((response: Response) => {
                if (!response.ok) {
                    tile.setState(TileState.ERROR);
                    zoomToSafeLevel(map);
                    return null;
                }

                const contentType = response.headers.get('content-type');

                if (contentType && !contentType.toLowerCase().startsWith('image/')) {
                    tile.setState(TileState.ERROR);
                    zoomToSafeLevel(map);
                    return null;
                }

                return response.blob();
            })
            .then((blob: Blob | null) => {
                if (!blob) {
                    return;
                }

                const currentZoom = map.getView().getZoom();

                if (isPlaceholderTile(blob, currentZoom)) {
                    tile.setState(TileState.ERROR);
                    zoomToSafeLevel(map);
                    return;
                }

                setImageTileBlob(tile, blob, TileState.ERROR);
            })
            .catch(() => {
                tile.setState(TileState.ERROR);
                zoomToSafeLevel(map);
            });
    };
}

